import { Textarea } from '@ibsys/kit';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '@/entities/profile';
import { useUpdateAnonymizerConfig, useAnonymizerConfig } from '@/entities/anonymizer-config';
import type { PhoneConfig } from '@/entities/anonymizer-config';
import { ConfigFormLayout } from './ConfigFormLayout';

type FormValues = {
  // Этап 1 (stage2)
  inlinePhoneRegex: string;
  contextMarkers: string;
  lookbackWindow: string;
  exclusionChars: string;
  // Этап 2 (stage3)
  keywordRegex: string;
  birthKeywordRegex: string;
  shortDigitsRegex: string;
  dateLikeRegex: string;
  phoneContextMarkers: string;
  phoneLookbackWindow: string;
  otherContextMarkers: string;
  otherLookbackWindow: string;
};

const getDefaultValues = (config: PhoneConfig | undefined): FormValues => ({
  inlinePhoneRegex: config?.stage2.inlinePhoneRegex ?? '',
  contextMarkers: config?.stage2.contextMarkers?.join('\n') ?? '',
  lookbackWindow: String(config?.stage2.lookbackWindow ?? ''),
  exclusionChars: config?.stage2.exclusionChars?.join('\n') ?? '',
  keywordRegex: config?.stage3.keywordRegex ?? '',
  birthKeywordRegex: config?.stage3.birthKeywordRegex ?? '',
  shortDigitsRegex: config?.stage3.shortDigitsRegex ?? '',
  dateLikeRegex: config?.stage3.dateLikeRegex ?? '',
  phoneContextMarkers: config?.stage3.phoneContextMarkers?.join('\n') ?? '',
  phoneLookbackWindow: String(config?.stage3.phoneLookbackWindow ?? ''),
  otherContextMarkers: config?.stage3.otherClassContextMarkers?.join('\n') ?? '',
  otherLookbackWindow: String(config?.stage3.otherClassLookbackWindow ?? ''),
});

type Props = {
  data: unknown;
  onUnsavedChange?: (dirty: boolean) => void;
};

export const PhoneConfigForm: FC<Props> = ({ data, onUnsavedChange }) => {
  const { t } = useTranslation('anonymizerConfig');
  const canEdit = useHasPermission('ADMIN_ANONYMIZE_CONFIG');
  const config = data as PhoneConfig | undefined;
  const { mutate: updateConfig, isPending } = useUpdateAnonymizerConfig();
  const { refetch } = useAnonymizerConfig('phone');

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: getDefaultValues(config),
  });

  useEffect(() => {
    if (config) {
      reset(getDefaultValues(config));
    }
  }, [config, reset]);

  useEffect(() => {
    onUnsavedChange?.(isDirty);
  }, [isDirty, onUnsavedChange]);

  const onSubmit = handleSubmit((values) => {
    if (!config) return;
    const body: PhoneConfig = {
      stage2: {
        inlinePhoneRegex: values.inlinePhoneRegex || config.stage2.inlinePhoneRegex,
        contextMarkers: values.contextMarkers
          ? values.contextMarkers.split('\n').filter(Boolean)
          : config.stage2.contextMarkers,
        lookbackWindow: values.lookbackWindow ? Number(values.lookbackWindow) : config.stage2.lookbackWindow,
        exclusionChars: values.exclusionChars
          ? values.exclusionChars.split('\n').filter(Boolean)
          : config.stage2.exclusionChars,
      },
      stage3: {
        keywordRegex: values.keywordRegex || config.stage3.keywordRegex,
        birthKeywordRegex: values.birthKeywordRegex || config.stage3.birthKeywordRegex,
        shortDigitsRegex: values.shortDigitsRegex || config.stage3.shortDigitsRegex,
        dateLikeRegex: values.dateLikeRegex || config.stage3.dateLikeRegex,
        phoneContextMarkers: values.phoneContextMarkers
          ? values.phoneContextMarkers.split('\n').filter(Boolean)
          : config.stage3.phoneContextMarkers,
        phoneLookbackWindow: values.phoneLookbackWindow
          ? Number(values.phoneLookbackWindow)
          : config.stage3.phoneLookbackWindow,
        otherClassContextMarkers: values.otherContextMarkers
          ? values.otherContextMarkers.split('\n').filter(Boolean)
          : config.stage3.otherClassContextMarkers,
        otherClassLookbackWindow: values.otherLookbackWindow
          ? Number(values.otherLookbackWindow)
          : config.stage3.otherClassLookbackWindow,
      },
    };
    updateConfig({ className: 'phone', body });
  });

  const handleReset = () => {
    reset(getDefaultValues(config));
    refetch();
  };

  return (
    <ConfigFormLayout
      title={t('classes.phone')}
      isDirty={isDirty}
      onSave={onSubmit}
      onReset={handleReset}
      isSaving={isPending}
    >
      <div className="flex flex-col gap-6 [&_label]:text-foreground">
        {/* Этап 1 */}
        <h3 className="text-lg font-medium">{t('fields.stage1Title')}</h3>
        <Textarea
          control={control}
          name="inlinePhoneRegex"
          label={t('fields.fullMatchPattern')}
          disabled={!canEdit}
          rows={3}
        />
        <Textarea
          control={control}
          name="contextMarkers"
          label={t('fields.contextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="exclusionChars"
          label={t('fields.exclusionChars')}
          disabled={!canEdit}
          rows={2}
        />
        <Textarea
          control={control}
          name="lookbackWindow"
          label={t('fields.lookbackWindow')}
          disabled={!canEdit}
          rows={1}
        />

        {/* Этап 2 */}
        <h3 className="text-lg font-medium">{t('fields.stage2Title')}</h3>
        <h4 className="text-base font-medium">{t('fields.keywordMatchPatterns')}</h4>
        <Textarea control={control} name="keywordRegex" label={t('fields.phoneNumber')} disabled={!canEdit} rows={3} />
        <Textarea
          control={control}
          name="birthKeywordRegex"
          label={t('fields.birthDateKeyword')}
          disabled={!canEdit}
          rows={3}
        />

        <h4 className="text-base font-medium">{t('fields.digitReplicaPatterns')}</h4>
        <Textarea
          control={control}
          name="dateLikeRegex"
          label={t('fields.shortDateReplicas')}
          disabled={!canEdit}
          rows={3}
        />
        <Textarea
          control={control}
          name="shortDigitsRegex"
          label={t('fields.otherDigits')}
          disabled={!canEdit}
          rows={3}
        />

        <Textarea
          control={control}
          name="phoneContextMarkers"
          label={t('fields.phoneContextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="phoneLookbackWindow"
          label={t('fields.phoneLookbackWindow')}
          disabled={!canEdit}
          rows={1}
        />
        <Textarea
          control={control}
          name="otherContextMarkers"
          label={t('fields.otherContextMarkers')}
          disabled={!canEdit}
          rows={5}
        />
        <Textarea
          control={control}
          name="otherLookbackWindow"
          label={t('fields.otherLookbackWindow')}
          disabled={!canEdit}
          rows={1}
        />
      </div>
    </ConfigFormLayout>
  );
};
